import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import {
    ActivatedRoute,
    Router,
} from '@angular/router';
import { AppStateFacade } from '@app-state';
import { PageMetadataRegistry } from '@app/router';
import {
    ControlConfig,
    FieldConfigFn,
    UpsertPageComponent,
    UpsertPageDefaultValues,
} from '@components';
import {
    ParameterValidators,
    UiParameterCollectionComponent,
} from '@components/shared/ui-parameter-collection/ui-parameter-collection.component';
import { CustomCron } from '@components/ui-cron/cron.models';
import { IFormAbstractControl } from '@components/ui-upsert-page/ui-form.component';
import {
    IProcess,
    IQueue,
    ITriggerUpsert,
    JobStartStrategy,
    StopStrategy,
    TriggerType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    MAX_NUMBER_OF_JOBS,
    MAX_NUMBER_OF_ROBOTS,
} from '@pages/shared/constants';
import { TriggerUpsertPageService } from '@pages/triggers/upsert/trigger.upsert.page.service';
import { TransactionService } from '@services';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import { UiSuggestComponent } from '@uipath/angular/components/ui-suggest';
import {
    ISuggestValue,
    ISuggestValueData,
} from '@uipath/angular/components/ui-suggest/models';
import { decodeParams } from '@utils/encode.utils';
import { getProperty } from '@utils/object.utils';
import UiValidators from '@validators';

import { Observable } from 'rxjs';
import {
    filter,
    finalize,
    map,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import {
    ITEMS_ACTIVATION_MAX,
    ITEMS_PER_JOB_ACTIVATION_MAX,
    ITriggerUpsertContext,
    MAX_JOBS_ACTIVATION_MAX,
    QUEUE_TRIGGER_SPECIFIC_FIELDS,
    START_STRATEGY,
    START_STRATEGY_JOB_MIN_NO_OF_JOBS,
    STOP_DAYS_MAX,
    STOP_HOURS_MAX,
    STOP_MINUTES_MAX,
    TIME_TRIGGER_SPECIFIC_FIELDS,
    TRIGGER_FORM_FIELDS,
    TRIGGER_MAX_NAME_LENGTH,
    TRIGGER_TYPES,
} from './trigger.upsert.page.models';

@Component({
    templateUrl: './trigger.upsert.page.html',
    styleUrls: ['./trigger.upsert.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [TransactionService],
    encapsulation: ViewEncapsulation.None,
})
export class TriggerUpsertPageComponent
    extends UpsertPageComponent<ITriggerUpsert>
    implements OnInit {

    public fields = TRIGGER_FORM_FIELDS;
    public startNoOfJobs = START_STRATEGY_JOB_MIN_NO_OF_JOBS;
    public maxNoOfJobs = MAX_NUMBER_OF_JOBS;
    public maxNoOfRobots = MAX_NUMBER_OF_ROBOTS;

    public JobStartStrategy = JobStartStrategy;
    public StopStrategy = StopStrategy;

    public get defaults(): UpsertPageDefaultValues<ITriggerUpsert> {
        return {
            type: this._formContext.type,
            name: '',
            enabled: true,

            useCalendar: false,
            externalJobKey: '',

            environmentId: NaN,

            // cron junk
            startProcessCron: '',
            startProcessCronDetails: null,

            disableAfter: false,
            stopProcessDate: null,

            startStrategy: -1,
            startStrategyRadio: JobStartStrategy.AllRobots,

            executorRobots: [],
            inputArguments: {},

            itemsActivationThreshold: 1,
            itemsPerJobActivationTarget: 1,
            maxJobsForActivation: 1,

            stopEnabled: false,
            stopDays: 0,
            stopHours: 0,
            stopMinutes: 0,
            stopStrategy: null,

            calendar: this.isMultipleCalendarsEnabled ?
                this._defaultCalendar : undefined,
            queue: this._defaultQueueFormContext,
            timezone: this._defaultTimezone,
            process: this._defaultProcessFormContext,
        };
    }

    public get validators() {
        return {
            name: Validators.compose([
                UiValidators.required(),
                Validators.maxLength(TRIGGER_MAX_NAME_LENGTH),
            ]),
            process: UiValidators.required(),
            timezone: UiValidators.required(),
            startStrategy: this._maxNoOfJobsDynamicValidator,
            stopDays: UiValidators.range(0, STOP_DAYS_MAX),
            stopHours: UiValidators.range(0, STOP_HOURS_MAX),
            stopMinutes: UiValidators.range(0, STOP_MINUTES_MAX),
            queue: UiValidators.required(),
            itemsActivationThreshold: UiValidators.range(1, ITEMS_ACTIVATION_MAX),
            itemsPerJobActivationTarget: UiValidators.range(1, ITEMS_PER_JOB_ACTIVATION_MAX),
            maxJobsForActivation: UiValidators.range(1, MAX_JOBS_ACTIVATION_MAX),
        };
    }

    public get isSubmitDisabled() {
        return this._isSubmitDisabled ||
            !!this.parameterCollectionGrid &&
            this.parameterCollectionGrid.isOpen;
    }

    public get timezone(): string {
        return this._translate.instant('trigger_modal_timezone_info', {
            timezone: this.getField('timezone').value[0].text ||
                this._translate.instant('trigger_modal_timezone_server_time'),
        });
    }

    public get environmentId() {
        return this._formContext.environmentId;
    }

    public get isTimeTriggerContext() {
        return this._formContext.type === TriggerType.Time;
    }

    public get isTenantTimezone() {
        const timezone = this.getField('timezone').value[0];

        return timezone &&
            this._upsertService.timezoneId === timezone.id;
    }

    public get isMultipleCalendarsEnabled() {
        return this._upsertService.isMultipleCalendarsEnabled;
    }

    public get isTimeTrigger() {
        if (!this.form) { return false; }

        const type = this.getField('type').value;
        return !!type && type === TriggerType.Time;
    }

    public get isTargetSpecificRobots() {
        if (!this.form) { return false; }

        return this.getField('startStrategyRadio').value === JobStartStrategy.SpecificRobots;
    }

    public get isTargetJobsCount() {
        if (!this.form) { return false; }

        return this.getField('startStrategyRadio').value === JobStartStrategy.JobsCount;
    }

    public get paramCollectionInput() {
        if (
            !this.form ||
            !this.getField('process').value.length
        ) { return []; }

        const process = this.getField('process');
        return process.value[0].data!.arguments.input || [];
    }

    private get _defaultQueueFormContext(): ISuggestValue[] {
        return this._formContext.queue ?
            [{
                id: this._formContext.queue.id,
                text: this._formContext.queue.name,
            }] :
            [];
    }

    private get _defaultProcessFormContext(): ISuggestValueData<IProcess>[] {
        return this._formContext.process ?
            [{
                id: this._formContext.process.id,
                text: this._formContext.process.name,
                data: this._formContext.process.data,
            }] :
            [];
    }

    private get _defaultTimezone(): ISuggestValue[] {
        return this.timezones ?
            [this.timezones.find(tz => tz.id === this._upsertService.timezoneId)!] :
            [];
    }

    private get _defaultCalendar(): ISuggestValue[] {
        return [];
    }

    private get _submit$() {
        if (!this.form) { throw new Error('Form is undefined'); }

        if (!this.isTimeTrigger) {
            // FIXME: CRON - for queue trigger ensure we send the default cron ...
            this.startProcessCronObj = this._upsertService.parseCron();
        }

        this.startProcessCronObj!.normalize();

        const trigger = {
            ...this.form.value,
            // FIXME: CRON
            startProcessCronDetails: JSON.stringify(this.startProcessCronObj),
            startProcessCron: this.startProcessCronObj!.cronExpression as string,
        };

        // ensure we reset executorRobots if strategy is not SpecificRobots
        // this is not handled within the form in order to persist selected state
        //  if user switches between strategies or types
        if (trigger.startStrategyRadio !== JobStartStrategy.SpecificRobots) {
            trigger.executorRobots = [];
        }

        return this.isEditMode ?
            this._upsertService.update(this.id, trigger) :
            this._upsertService.add(trigger);
    }

    @ViewChild(UiParameterCollectionComponent, { static: false })
    public parameterCollectionGrid?: UiParameterCollectionComponent;

    @ViewChild('calendarPicker', { static: false })
    public calendarPickerSuggest?: UiSuggestComponent;

    public form?: FormGroup;

    public processChangeDisabled = false;

    public startProcessCronObj?: CustomCron;
    public timezones: ISuggestValue[] = [];
    public calendarNames: ISuggestValue[] = [];

    public disableQueueTrigger$ = this._appState.user.permissions
        .isGranted('Queues.View')
        .pipe(map(permission => !permission));

    public hasRobotsViewPermissions$ = this._appState.user.permissions
        .isGranted('Robots.View');

    public triggerOptions$ = this.disableQueueTrigger$.pipe(
        map(disableQueueTrigger => TRIGGER_TYPES
            .map(type => {
                if (type.id === TriggerType.Queue) {
                    type.disabled = disableQueueTrigger;
                }

                return type;
            }),
        ),
    );

    public processSuggest$ = this._upsertService.processesSuggest$;

    protected _initialContext$: Observable<any>[] = [
        this._upsertService.getTimezones()
            .pipe(
                tap(timezones => this.timezones = timezones),
            ),
    ];

    private _formContext!: ITriggerUpsertContext;

    constructor(
        _route: ActivatedRoute,
        _router: Router,
        _appState: AppStateFacade,
        private _fb: FormBuilder,
        private _snack: UiSnackBarService,
        private _upsertService: TriggerUpsertPageService,
        private _translate: TranslateService,
        private _pageRegistry: PageMetadataRegistry,
        private _cd: ChangeDetectorRef,
    ) {
        super(_route, _router, _appState);
        this._setFormContext();
    }

    ngOnInit() {
        super.ngOnInit();

        this._appState.user
            .folder
            .change$.pipe(
                tap(() => {
                    if (this.isEditMode) {
                        this._forceNavigateBaseUrl();
                        return;
                    }

                    this.getFields('process', 'queue')
                        .forEach(f => f.setValue([]));
                    this._formContext = {
                        type: this.getField('type').value,
                    };
                }),
                takeUntil(this._destroyed$),
            ).subscribe();
    }

    public onSubmit() {
        if (this.isSubmitDisabled) { return; }

        this.loading$.next(true);
        this._submit$.pipe(
            finalize(() => this.loading$.next(false)),
        ).subscribe(
            () => {
                this._snack.success(this._translate.instant('save_successful'));
                this._forceNavigateBaseUrl();
            },
            error => {
                if (error.status === 403) {
                    this._forceNavigateBaseUrl();
                }
            },
        );
    }

    public changedTrigger(newTrigger: TriggerType) {
        const isTimeTrigger = newTrigger === TriggerType.Time;

        this._updateTriggerTypeFields(isTimeTrigger);

        if (!isTimeTrigger) {
            // queue trigger cannot have stopJob enabled
            this.getField('stopEnabled').setValue(false);
            // ensure that any other types have the start strategy set to all robots
            this.getField('startStrategy').setValue(-1);
            this.getField('startStrategyRadio').setValue(JobStartStrategy.AllRobots);
        } else {
            // disabling process change is only valid for queue triggers
            this.processChangeDisabled = false;
        }

        this.getField('type').markAsPristine();
    }

    public changedDisableAfter(disableAfter: boolean) {
        const stopProcessDate = this.getField('stopProcessDate');

        if (disableAfter) {
            stopProcessDate.enable();
        } else {
            stopProcessDate.disable();
            stopProcessDate.setValue(null);
        }
    }

    public changedStrategy({ value }: MatRadioChange) {
        this.getField('startStrategy')
            .setValue(START_STRATEGY.get(value)!);
    }

    public changedTimezone(newTimezone: ISuggestValue) {
        if (this._upsertService.timezoneId === newTimezone.id) { return; }

        this.getField('useCalendar').setValue(false);
    }

    public changedCalendar(calendar: ISuggestValue) {
        this.getField('useCalendar').setValue(!!calendar);
    }

    protected _getInstance = () =>
        this._upsertService.getInstanceData(this.id, this.timezones)

    protected _initForm(
        controlsConfig: ControlConfig<ITriggerUpsert>,
        _: FieldConfigFn<ITriggerUpsert>,
        trigger?: ITriggerUpsert) {

        if (trigger) {
            this._pageRegistry.setCustomTitle(trigger.name);
            this._formContext.environmentId = trigger.environmentId;
            this._formContext.type = trigger.type;
        }

        this.startProcessCronObj = this._upsertService
            .parseCron(trigger ?
                trigger.startProcessCronDetails :
                undefined,
            );

        const configCalendarId = getProperty(controlsConfig, 'calendar.0.0.id');
        const defaultCalendarValue = this.isMultipleCalendarsEnabled ?
            [this.calendarNames.filter(calendar => calendar.id === configCalendarId)] :
            undefined;

        this.form = this._fb.group(
            {
                ...controlsConfig,

                calendar: defaultCalendarValue,

                // FIXME: refactor cron :(
                cron: this._fb.group({
                    minutelyAtMinute: [[]],
                    hourlyEveryHours: [this.startProcessCronObj.hourly.frequencyInHours],
                    hourlyAtMinute: [this.startProcessCronObj.hourly.atMinute],
                    dailyAtHour: [this.startProcessCronObj.daily.atHour],
                    dailyAtMinute: [this.startProcessCronObj.daily.atMinute],
                    weeklyAtHour: [this.startProcessCronObj.weekly.atHour],
                    weeklyAtMinute: [this.startProcessCronObj.weekly.atMinute],
                    monthlyAtHour: [this.startProcessCronObj.monthly.atHour],
                    monthlyAtMinute: [this.startProcessCronObj.monthly.atMinute],
                    monthlyFrequency: [this.startProcessCronObj.monthly.frequencyInMonths],
                    advancedCron: [this.startProcessCronObj.advancedCronExpression],
                }),
            }, {
                validator: Validators.compose([
                    this._nonNullIntervalValidator,
                    this._requiredRobotsValidator,
                ]),
            });

        this._updateTriggerTypeFields(this.isTimeTriggerContext);

        const queue = this.getField('queue');
        queue.valueChanges
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(this._changedQueue);

        const process = this.getField('process');
        process.valueChanges
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(this._changedProcess);

        this._addParametersValidator();

        if (!this.isTimeTrigger) {
            this.getField('queue').updateValueAndValidity();
        }

        this.disableQueueTrigger$.pipe(
            filter(Boolean),
            tap(() => {
                const type = this.getField('type');
                if (type.value !== TriggerType.Queue) { return; }

                if (this.isEditMode) {
                    this._snack.error(this._translate.instant('page_access_denied'));
                    this._forceNavigateBaseUrl();
                    return;
                }

                type.setValue(TriggerType.Time);
            }),
            takeUntil(this._destroyed$),
        ).subscribe();

        this.loading$.next(false);
    }

    private _changedQueue = ([selected]: ISuggestValueData<IQueue>[]) => {
        if (!selected || this.isTimeTrigger) {
            this.processChangeDisabled = false;
            return;
        }

        this._upsertService.getQueueDefinition(selected.id as number)
            .pipe(
                tap(queue => {
                    // FIXME: get rid of this, we should mark the form fields as disabled, not the widgets
                    this.processChangeDisabled = queue.slaMinutes > 0;
                    this._cd.detectChanges();
                }),
                filter(queue => queue.releaseId != null),
                switchMap(queue => this._upsertService.getProcess(queue.releaseId!)),
                takeUntil(this._destroyed$),
            )
            .subscribe(queueProcess => {
                const processControl = this.getField('process');
                const processValue = processControl.value && processControl.value[0];

                if (processValue && processValue.id === queueProcess.id) {
                    return;
                }

                processControl.setValue([{
                    data: queueProcess,
                    id: queueProcess.id,
                    text: queueProcess.name,
                }]);
            });
    }

    private _changedProcess = ([selected]: ISuggestValueData<IProcess>[]) => {
        if (!selected) { return; }
        this._formContext.environmentId = selected.data!.environmentId;

        const inputArgs = this.getField('inputArguments');

        // reset previous values and validators if any
        inputArgs.clearValidators();
        inputArgs.setValue(this.defaults.inputArguments);

        this._addParametersValidator();
    }

    private _addParametersValidator() {
        const processValue = this.getField('process').value;
        if (!processValue.length) { return; }

        const [{ data }] = processValue;

        if (data!.arguments) {
            const inputArgs = this.getField('inputArguments');
            inputArgs.setValidators(ParameterValidators.required(data!.arguments.input));
            inputArgs.updateValueAndValidity();
        }

    }

    private _updateTriggerTypeFields(isTimeTrigger: boolean) {
        const queueInputs = QUEUE_TRIGGER_SPECIFIC_FIELDS;
        const timeInputs = [
            ...TIME_TRIGGER_SPECIFIC_FIELDS,
            // FIXME: refactor cron
            'cron' as keyof ITriggerUpsert,
        ];

        if (isTimeTrigger) {
            this.disableFields(...queueInputs);
            this.enableFields(...timeInputs);
        } else {
            this.enableFields(...queueInputs);
            this.disableFields(...timeInputs);
        }
    }

    private _maxNoOfJobsDynamicValidator = (startStrategyFormControl: FormControl) => {
        if (!this.form || !this.isTargetJobsCount) { return null; }

        return Validators.compose([
            UiValidators.range(1, this.maxNoOfJobs, true),
            UiValidators.required(),
        ])!(startStrategyFormControl);
    }

    private _nonNullIntervalValidator = () => {
        if (!this.form || !this.getField('stopEnabled').value) { return null; }

        const stopFields: IFormAbstractControl<number>[] = this.getFields(
            'stopDays',
            'stopHours',
            'stopMinutes');

        return !stopFields.find(f => f.value > 0) ?
            { zeroMinutes: true } :
            null;
    }

    private _requiredRobotsValidator = () => {
        if (!this.form || !this.isTargetSpecificRobots) { return null; }

        const selectedRobots = this.getField('executorRobots').value;

        return selectedRobots.length === 0 ?
            { requiredRobotsNotSelected: true } :
            null;
    }

    private _setFormContext() {
        const queue = this._route.snapshot.queryParamMap.get('queue');
        const process = this._route.snapshot.queryParamMap.get('process');
        const type = this._route.snapshot.queryParamMap.get('type');

        this._formContext = {
            type: type !== null ?
                Number(type) as TriggerType :
                TriggerType.Time,
            ...(queue ? { queue: decodeParams(queue) } : {}),
            ...(process ? { process: decodeParams(process) } : {}),
        };

        if (this._formContext.process) {
            this._initialContext$.push(
                this._upsertService
                    .getProcess(this._formContext.process.id).pipe(
                        tap(p => this._formContext.process!.data = p),
                    ),
            );
        }

        if (this._formContext.queue) {
            this._initialContext$.push(
                this._upsertService.getQueueDefinition(this._formContext.queue.id).pipe(
                    tap(q => this._formContext.queue!.data = q),
                ),
            );
        }

        this._initialContext$.push(
            this._upsertService
                .getCalendarNames().pipe(
                    tap(calendars => this.calendarNames = calendars),
                ),
        );
    }
}
